import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  sendMessage,
  getMessage,
  getUnreadChatCount, // ✅ ADD THIS
} from "../../../redux/reducers/UserSlice/UserSlice";
import { resetCount } from "../../../redux/reducers/ChatReducer/ChatSlice";
import type {
  Chat,
  Message,
} from "../../../redux/reducers/ChatReducer/ChatSliceTypes";
import { Booking, BookingQuery } from "../types/messages.types";
import {
  arrangeMessages,
  scrollToBottom,
  isScrolledToTop,
  restoreScrollPosition,
} from "../utils/messageUtils";
import { UI_TEXT, SCROLL } from "../constants/messages.constants";

interface UseMessagesReturn {
  messages: Message[];
  message: string;
  messageLoading: boolean;
  isUploading: boolean;
  page: number;
  totalPages: number;
  booking: Booking | null;
  bookingQuery: BookingQuery | null;
  bookingId: number | null;
  bookingQueryId: number | null;
  messageContainerRef: React.RefObject<HTMLDivElement>;
  setMessage: (message: string) => void;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  handleSendMessage: (
    selectedChat: Chat | null,
    messageText: string,
    attachments: (string | File)[],
    clearAttachments: () => void,
    restoreData: (msg: string, att: (string | File)[]) => void
  ) => Promise<void>;
  getAllMessages: (chatId: number, page: number) => Promise<void>;
  handleScroll: () => void;
  resetSelectedChatUnreadCount: (chatId: string | null) => void;
}

/**
 * Custom hook for message management
 * Handles fetching, sending, pagination, and scrolling of messages
 */
export const useMessages = (
  selectedChatId: string | null
): UseMessagesReturn => {
  const dispatch = useDispatch<AppDispatch>();
  const messageContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollPositionRef = useRef<number>(0);

  const [messages, setMessages] = useState<Message[]>([]);
  const [message, setMessage] = useState<string>("");
  const [messageLoading, setMessageLoading] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [booking, setBooking] = useState<any>(null);
  const [bookingQuery, setBookingQuery] = useState<any>(null);
  const [bookingId, setBookingId] = useState<any>(null);
  const [bookingQueryId, setBookingQueryId] = useState<any>(null);

  /**
   * Reset page when chat changes
   */
  useEffect(() => {
    setPage(1);
    setMessages([]);
    scrollPositionRef.current = 0;
  }, [selectedChatId]);

  /**
   * Auto-scroll to bottom when new messages arrive
   */
  useEffect(() => {
    scrollToBottom(messageContainerRef, SCROLL.SCROLL_TO_BOTTOM_DELAY);
  }, [messages, booking]);

  /**
   * Fetch messages when chat or page changes
   */
  useEffect(() => {
    if (selectedChatId && page) {
      getAllMessages(parseInt(selectedChatId), page);

      const container = messageContainerRef.current;
      if (container && page > 1) {
        restoreScrollPosition(container, scrollPositionRef.current);
      }
    }

    if (selectedChatId && page === 1) {
      scrollPositionRef.current = 0;
    }
  }, [selectedChatId, page]);

  /**
   * Fetch all messages for a chat
   */
  const getAllMessages = async (chatId: number, pageNumber: number) => {
    const container = messageContainerRef.current;
    if (container && pageNumber > 1) {
      scrollPositionRef.current = container.scrollTop;
    }
    setMessageLoading(true);

    try {
      const action = await dispatch(getMessage({ chatId, page: pageNumber }));
      if (action?.payload?.success) {
        const newData = action?.payload?.message || [];
        setMessages((prevMessages) => {
          const updatedMessages = arrangeMessages(
            pageNumber === 1 ? [] : prevMessages,
            newData
          );
          return updatedMessages;
        });

        const bookingData = action?.payload?.chat?.booking;
        if (bookingData) {
          setBooking(bookingData);
          setBookingId(bookingData?.id);
        } else {
          setBooking(null);
          setBookingId(null);
        }

        const bookingQueryData = action?.payload?.chat?.booking_query;
        if (bookingQueryData) {
          setBookingQuery(bookingQueryData);
          setBookingQueryId(bookingQueryData?.id);
        } else {
          setBookingQuery(null);
          setBookingQueryId(null);
        }

        if (action?.payload?.meta?.total_pages) {
          setTotalPages(action.payload.meta.total_pages);
        }
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
      toast.error(UI_TEXT.ERRORS.LOAD_MESSAGES_FAILED);
    } finally {
      setMessageLoading(false);
    }
  };

  /**
   * Handle sending a message
   */
  const handleSendMessage = async (
    selectedChat: Chat | null,
    messageText: string,
    attachments: (string | File)[],
    clearAttachments: () => void,
    restoreData: (msg: string, att: (string | File)[]) => void
  ) => {
    if (!messageText.trim() && attachments.length === 0) {
      toast.warning(UI_TEXT.ERRORS.EMPTY_MESSAGE);
      return;
    }

    if (!selectedChat?.id) {
      toast.error(UI_TEXT.ERRORS.NO_CHAT_SELECTED);
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append("message[body]", messageText || "");
    formData.append("chat_id", selectedChat.id.toString());

    attachments.forEach((attachment) => {
      if (attachment instanceof File) {
        formData.append("message[attachments][]", attachment);
      } else {
        console.error("Invalid attachment type:", attachment);
      }
    });

    // Store current message and attachments for potential restore
    const tempMessage = messageText;
    const tempAttachments = [...attachments];

    // Clear input immediately for better UX
    setMessage("");
    clearAttachments();

    try {
      const action = await dispatch(sendMessage(formData));

      if (action?.payload?.success) {
        // Refetch messages to get the complete updated list
        await getAllMessages(selectedChat.id, 1);

        // Scroll to bottom after message is sent
        scrollToBottom(messageContainerRef, SCROLL.SCROLL_TO_BOTTOM_DELAY);
      } else {
        toast.error(UI_TEXT.ERRORS.SEND_FAILED);
        // Restore message on failure
        restoreData(tempMessage, tempAttachments);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error(UI_TEXT.ERRORS.SEND_ERROR);
      // Restore message on error
      restoreData(tempMessage, tempAttachments);
    } finally {
      setIsUploading(false);
    }
  };

  /**
   * Handle scroll for pagination
   */
  const handleScroll = () => {
    const container = messageContainerRef.current;

    if (
      container &&
      isScrolledToTop(messageContainerRef) &&
      page < totalPages
    ) {
      if (messageLoading) return;
      setPage((prevPage) => prevPage + 1);
    }
  };

/**
 * Reset unread count for selected chat
 * Marks as read and refreshes chat list
 */
const resetSelectedChatUnreadCount = async (chatId: string | null) => {
  if (!chatId) return;

  console.log("🔵 Resetting unread count for chat:", chatId);

  // ✅ Optimistically update Redux state immediately
  dispatch(resetCount(chatId));

  try {
    // ✅ Small delay for backend to process read_at update
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // ✅ Refresh unread count from backend
    await dispatch(getUnreadChatCount());
    
    console.log("🟢 Unread count updated successfully");
    
    // ✅ RETURN SUCCESS so parent can refresh chat list
    return true;
  } catch (error) {
    console.error("🔴 Error resetting unread count:", error);
    return false;
  }
};

  return {
    messages,
    message,
    messageLoading,
    isUploading,
    page,
    totalPages,
    booking,
    bookingQuery,
    bookingId,
    bookingQueryId,
    messageContainerRef,
    setMessage,
    setMessages,
    handleSendMessage,
    getAllMessages,
    handleScroll,
    resetSelectedChatUnreadCount,
  };
};
